import { memo, useEffect, useState } from 'react';
import { IconClose, IconWallet } from '../icons';
import { formatMoney } from '../format';

export interface NotEnoughMoneyModalProps {
  open: boolean;
  /**
   * Cost of the attempted spin in display units (e.g. 1.50). When provided,
   * the modal shows a "You need X · You have Y" line so the player understands
   * how much short they are. Both `required` and `balance` must be present
   * for the line to render.
   */
  required?: number;
  /** Current balance in display units. */
  balance?: number;
  /** Currency symbol or ISO code appended to amounts. Default: `'$'`. */
  currency?: string;
  /** Title of the popup. Default: `'Not enough money'`. */
  title?: string;
  /**
   * Body copy below the title. When omitted, the modal renders the default
   * "You don't have enough to make this spin." line plus (if `required` and
   * `balance` are set) the shortfall summary.
   */
  message?: string;
  /** Label of the dismissing button. Default: `'Close'`. */
  closeLabel?: string;
  /** Label of the primary CTA. Default: `'Go to Bank'`. */
  bankLabel?: string;
  /** Fired by the close button and the backdrop tap. */
  onClose: () => void;
  /**
   * Fired by the primary CTA. The shell does NOT auto-close after this fires —
   * consumers are free to navigate away or surface a deposit flow inside the
   * popup; if they want the popup to dismiss too, they should call `onClose`
   * themselves. When undefined, the bank button is hidden (no point showing a
   * dead button).
   */
  onGoToBank?: () => void;
}

/**
 * Bottom-sheet popup shown when the player tries to spin without enough
 * balance. Mirrors PickerModal's transitions/scrim so it feels consistent
 * with the bet / autospin sheets, but with a fixed two-button footer
 * (Close / Go to Bank) instead of an option grid.
 *
 * The modal is purely presentational — it does not know about iframes,
 * postMessage, or the game's spin pipeline. The SlotUI shell drives `open`
 * from inbound `game.showNotEnoughMoney` messages and forwards the bank CTA
 * to the consumer via the `onGoToBank` SlotUIActions callback.
 */
// Memoized — always mounted (gated by `open`), so without this it'd
// re-render on every unrelated SlotUI render even while closed. Paired
// with SlotUI passing stable onClose/onGoToBank references.
export const NotEnoughMoneyModal = memo(function NotEnoughMoneyModal({
  open,
  required,
  balance,
  currency = '$',
  title = 'Not enough money',
  message,
  closeLabel = 'Close',
  bankLabel = 'Go to Bank',
  onClose,
  onGoToBank,
}: NotEnoughMoneyModalProps) {
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);

  // Same mount/unmount choreography as PickerModal — render a frame with
  // `transform: translateY(100%)` first, flip to `translateY(0)` next frame
  // so the CSS transition has something to animate from. On close, hold the
  // node mounted for the transition duration before unmounting.
  useEffect(() => {
    if (open) {
      setMounted(true);
      requestAnimationFrame(() =>
        requestAnimationFrame(() => setVisible(true)),
      );
    } else if (mounted) {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), 280);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, mounted]);

  if (!mounted) return null;

  const showShortfall =
    typeof required === 'number' &&
    Number.isFinite(required) &&
    typeof balance === 'number' &&
    Number.isFinite(balance);
  const defaultMessage = "You don't have enough to make this spin.";

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 70, // above PickerModal (60) so a stale bet sheet doesn't cover us.
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          // See PickerModal — dropped backdrop-filter to avoid iOS Safari
          // compositor crashes when the scrim is overlaid on a live iframe.
          background: 'rgba(0,0,0,0.7)',
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.25s ease',
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reelay-nem-title"
        style={{
          position: 'relative',
          width: '100%',
          background: 'var(--surface)',
          borderTop: '1px solid var(--border-2)',
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          padding: '18px 20px 24px',
          boxShadow: 'var(--shadow)',
          transform: visible ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.32s cubic-bezier(0.37, 0, 0.63, 1)',
        }}
      >
        <div
          style={{
            width: 44,
            height: 4,
            borderRadius: 2,
            background: 'var(--border-2)',
            margin: '0 auto 14px',
          }}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 14,
          }}
        >
          <div
            id="reelay-nem-title"
            style={{
              fontSize: 13.2,
              fontWeight: 400,
              letterSpacing: 2,
              textTransform: 'uppercase',
              fontFamily: 'Oswald, sans-serif',
              color: 'var(--accent)',
            }}
          >
            {title}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <IconClose size={16} />
          </button>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 14,
            alignItems: 'flex-start',
            padding: '4px 4px 18px',
          }}
        >
          <div
            style={{
              flex: '0 0 44px',
              height: 44,
              borderRadius: '50%',
              background: 'var(--accent-soft)',
              color: 'var(--accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-hidden
          >
            <IconWallet size={22} />
          </div>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <div
              style={{
                fontSize: 15,
                lineHeight: 1.4,
                color: 'var(--text)',
                fontFamily: 'Oswald, sans-serif',
              }}
            >
              {message ?? defaultMessage}
            </div>
            {showShortfall && (
              <div
                style={{
                  marginTop: 8,
                  fontSize: 13,
                  color: 'var(--text-muted)',
                  fontFamily: 'Oswald, sans-serif',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px 12px',
                }}
              >
                <span>
                  Need:{' '}
                  <span style={{ color: 'var(--text)', fontWeight: 400 }}>
                    {formatMoney(required as number, currency)}
                  </span>
                </span>
                <span>
                  Have:{' '}
                  <span style={{ color: 'var(--text)', fontWeight: 400 }}>
                    {formatMoney(balance as number, currency)}
                  </span>
                </span>
              </div>
            )}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 10,
          }}
        >
          <button
            onClick={onClose}
            style={{
              flex: '1 1 0',
              padding: '14px 16px',
              background: 'var(--surface-2)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: 12,
              cursor: 'pointer',
              fontFamily: 'Oswald, sans-serif',
              fontSize: 15,
              fontWeight: 400,
              letterSpacing: 2,
              textTransform: 'uppercase',
              transition: 'background 0.15s ease, border-color 0.15s ease',
            }}
          >
            {closeLabel}
          </button>
          {onGoToBank && (
            <button
              onClick={onGoToBank}
              style={{
                flex: '1 1 0',
                padding: '14px 16px',
                background: 'var(--accent)',
                color: 'var(--accent-ink)',
                border: '1px solid var(--accent)',
                borderRadius: 12,
                cursor: 'pointer',
                fontFamily: 'Oswald, sans-serif',
                fontSize: 15,
                fontWeight: 400,
                letterSpacing: 2,
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                transition: 'background 0.15s ease',
              }}
            >
              <IconWallet size={18} />
              {bankLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
});
